/**
 * Cycle configure wizard — a guided path through what a cycle's tracks need
 * before sessions run. It is a simpler way to do what the cycle and track pages
 * do, never a separate place to keep answers: each step writes the records those
 * pages write, through the same calls.
 *
 * 1. Method (guide)   — writes nothing but the wizard's draft; ticks the criteria the
 *                       method scores on in step 2. A session picks its own method.
 * 2. Criteria         — each ticked track's Criteria rows (`cycle_attribute_configs`):
 *                       createCycleConfig / deleteCycleConfig, on Save.
 * 3. Weights          — each ticked track's criterion weights
 *                       (`cycle_attribute_configs.weight`, migration 1047):
 *                       saveTrackCriteriaWeights, as the track's Criteria tab, on Save.
 * 4. Value ranges     — the range on each of those rows: saveTrackRange, on Save.
 * 5. Value providers  — who may propose and approve values per criterion for the
 *                       cycle: the cycle page's Governance Overrides section
 *                       (`cycle_attribute_governance_overrides`), saved per change.
 * 6. People           — each ticked track's roster (`track_participants`):
 *                       addParticipant / removeParticipant, on Save.
 * 7. Tournament       — only with the tournaments module: the cycle's tournaments,
 *                       and a draft one for a track (`tournaments` + rounds) through
 *                       createTournamentWithRounds, the Tournament Builder's call.
 * 8. Review           — reads the tracks back; can lock criteria & scales on them
 *                       (set_track_criteria_and_ranges_locked); Finish closes the draft.
 *
 * `cycle_config_sessions` holds only the draft: the method chosen, and whether the
 * wizard was finished. (#1442 removed the Weights and Tournament steps for having no
 * real record; the owner ruled on 2026-11-02 to keep both and give each one.)
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useModuleGate } from '../../../lib/moduleGate';
import {
  Settings,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle,
  CheckCircle,
  Users,
  Target,
  Wrench,
  UserCheck,
  ListChecks,
  ClipboardCheck,
  Scale,
  Trophy,
} from 'lucide-react';
import { getDecision, type ToolsDecisionValue } from '../../../types/prioritization/cycleConfigSession';
import { useCycleConfigStore } from '../../../stores/prioritization/cycleConfigStore';
import { CriteriaConfigPanel } from './CriteriaConfigPanel';
import { AttributeRangePanel } from './AttributeRangePanel';
import { MethodSelectionPanel } from './MethodSelectionPanel';
import { CycleGovernanceOverrideSection } from './CycleGovernanceOverrideSection';
import { CyclePeoplePanel } from './CyclePeoplePanel';
import { CycleConfigReview } from './CycleConfigReview';
import { CycleWeightsPanel } from './CycleWeightsPanel';
import { CycleTournamentPanel } from './CycleTournamentPanel';
import { getToolById } from '../../../lib/toolIntegration';

// ============================================================================
// Types
// ============================================================================

export interface CycleConfigWizardProps {
  /** The configuration draft to work in */
  sessionId: string;

  /** Callback when wizard is closed */
  onClose?: () => void;

  /** Callback when the person finishes */
  onLocked?: () => void;

  /** Current user ID */
  userId: string;
}

type WizardStep =
  | 'methods'
  | 'criteria'
  | 'weights'
  | 'attribute_ranges'
  | 'value_providers'
  | 'participants'
  | 'tournament'
  | 'review';

interface StepConfig {
  id: WizardStep;
  label: string;
  icon: typeof Settings;
}

const ALL_WIZARD_STEPS: StepConfig[] = [
  { id: 'methods', label: 'Method', icon: Wrench },
  { id: 'criteria', label: 'Criteria', icon: Target },
  { id: 'weights', label: 'Weights', icon: Scale },
  { id: 'attribute_ranges', label: 'Value ranges', icon: ListChecks },
  { id: 'value_providers', label: 'Value providers', icon: UserCheck },
  { id: 'participants', label: 'People', icon: Users },
  { id: 'tournament', label: 'Tournament', icon: Trophy },
  { id: 'review', label: 'Review', icon: ClipboardCheck },
];

// ============================================================================
// Component
// ============================================================================

export function CycleConfigWizard({ sessionId, onClose, onLocked, userId }: CycleConfigWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('methods');
  // The Tournament step is drawn only when the tournaments module is on.
  const tournamentsEnabled = useModuleGate('tournaments');
  const WIZARD_STEPS = useMemo(
    () => ALL_WIZARD_STEPS.filter((s) => s.id !== 'tournament' || tournamentsEnabled),
    [tournamentsEnabled],
  );

  const session = useCycleConfigStore(state => state.getConfigSession(sessionId));
  const updateSessionState = useCycleConfigStore(state => state.updateSessionState);

  const currentStepIndex = WIZARD_STEPS.findIndex(s => s.id === currentStep);

  // A draft opened for the first time is in progress from then on.
  const sessionState = session?.state;
  useEffect(() => {
    if (sessionState === 'draft') updateSessionState(sessionId, 'in_progress');
  }, [sessionState, sessionId, updateSessionState]);

  // The method picked in step 1, and the criteria it scores on.
  const selectedToolInfo = useMemo(() => {
    if (!session) return null;
    const toolsValue = getDecision(session, 'tools')?.value as ToolsDecisionValue | undefined;
    const toolId = toolsValue?.primaryToolId || toolsValue?.selectedToolIds?.[0];
    const tool = toolId ? getToolById(toolId) : undefined;
    return tool ? { id: tool.id, name: tool.name, requiredAttributes: tool.requiresAttributes || [] } : null;
  }, [session]);

  const goTo = useCallback((index: number) => {
    if (index >= 0 && index < WIZARD_STEPS.length) setCurrentStep(WIZARD_STEPS[index].id);
  }, [WIZARD_STEPS]);

  const handleFinish = useCallback(() => {
    updateSessionState(sessionId, 'locked');
    onLocked?.();
  }, [sessionId, updateSessionState, onLocked]);

  if (!session) {
    return (
      <div className="p-6 text-center text-[var(--text-ter)]">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-[var(--coral)]" />
        <p>Configuration session not found</p>
      </div>
    );
  }

  if (session.state === 'locked') {
    return (
      <div className="p-6 text-center">
        <CheckCircle className="h-12 w-12 mx-auto mb-4 text-[var(--green)]" />
        <h3 className="text-lg font-medium text-[var(--text-pri)] mb-2">Finished</h3>
        <p className="text-[var(--text-sec)]">
          What this configuration set is on the cycle&apos;s tracks; their pages show it.
        </p>
      </div>
    );
  }

  const cycleId = session.cycleId;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
        <div className="flex items-center gap-3">
          <Settings className="h-6 w-6 text-[var(--text-ter)]" />
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">{session.name}</h2>
            <p className="text-sm text-[var(--text-ter)]">
              Set the criteria, weights, value ranges and people of this cycle&apos;s tracks, one step at a time
            </p>
          </div>
        </div>
      </div>

      {/* Step Navigation */}
      <nav aria-label="Steps" className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--bg-raised)] overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          {WIZARD_STEPS.map((step, index) => {
            const isActive = step.id === currentStep;
            const Icon = step.icon;
            const done = step.id === 'methods' && selectedToolInfo != null;
            return (
              <div key={step.id} className="flex items-center">
                <button
                  type="button"
                  onClick={() => goTo(index)}
                  aria-current={isActive ? 'step' : undefined}
                  className={`flex items-center gap-2 px-3 py-2 transition-colors ${
                    isActive
                      ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                      : 'bg-[var(--hover-tint)] text-[var(--text-ter)] hover:bg-[var(--hover-tint)]'
                  }`}
                >
                  {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  <span className="text-sm font-medium">{step.label}</span>
                </button>
                {index < WIZARD_STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-[var(--text-ter)] mx-1" />}
              </div>
            );
          })}
        </div>
      </nav>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {currentStep === 'methods' && <MethodSelectionPanel sessionId={sessionId} userId={userId} />}

        {currentStep === 'criteria' && (
          <CriteriaConfigPanel
            cycleId={cycleId}
            requiredCriteriaIds={selectedToolInfo?.requiredAttributes}
            selectedMethodName={selectedToolInfo?.name}
          />
        )}

        {currentStep === 'weights' && <CycleWeightsPanel cycleId={cycleId} />}

        {currentStep === 'attribute_ranges' && <AttributeRangePanel cycleId={cycleId} userId={userId} />}

        {currentStep === 'value_providers' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-[var(--text-pri)]">Value providers</h3>
              <p className="text-sm text-[var(--text-sec)] mt-1">
                Who may define and approve each criterion&apos;s range, and propose and accept its values,
                in this cycle. This is the cycle page&apos;s Governance Overrides; each change is saved as
                you make it, and a track can still set its own.
              </p>
            </div>
            <CycleGovernanceOverrideSection cycleId={cycleId} />
          </div>
        )}

        {currentStep === 'participants' && <CyclePeoplePanel cycleId={cycleId} userId={userId} />}

        {currentStep === 'tournament' && tournamentsEnabled && <CycleTournamentPanel cycleId={cycleId} userId={userId} />}

        {currentStep === 'review' && <CycleConfigReview cycleId={cycleId} />}
      </div>

      {/* Footer Navigation */}
      <div className="px-6 py-4 border-t border-[var(--rule)] bg-[var(--surface)] flex items-center justify-between">
        <button
          type="button"
          onClick={() => goTo(currentStepIndex - 1)}
          disabled={currentStepIndex === 0}
          className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>

        <div className="text-sm text-[var(--text-ter)]">
          Step {currentStepIndex + 1} of {WIZARD_STEPS.length}
        </div>

        {currentStep !== 'review' ? (
          <button
            type="button"
            onClick={() => goTo(currentStepIndex + 1)}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] flex items-center gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {onClose && (
              <button type="button" onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">
                Close
              </button>
            )}
            <button type="button" onClick={handleFinish} className="px-4 py-2 bg-[var(--slate)] text-white">
              Finish
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CycleConfigWizard;
